import React, { useState, useEffect } from 'react';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Button, Dialog, Select, MenuItem } from '@dsi/react-eds';
import ReactDOM from 'react-dom';
import api from '../../api/axiosInstance';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { AxiosError } from 'axios';

// Type definitions
interface Enm {
  id: string;
  name: string;
  category: string;
}

interface EiapCluster {
  id: string;
  name: string;
  linkedEnms: string[];
}

interface LinkEnmsDialogProps {
  open: boolean;
  clusterId: string;
  onSave: (clusterId: string, linkedEnmIds: string[]) => void;
  onClose: () => void;
}

const LinkEnmsDialog: React.FC<LinkEnmsDialogProps> = ({ open, clusterId, onSave, onClose }) => {
  const { addNotifications } = useSwobNotifications();
  const [enms, setEnms] = useState<Enm[]>([]);
  const [cluster, setCluster] = useState<EiapCluster | null>(null);
  const [selectedEnms, setSelectedEnms] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch ENMs and cluster data when dialog opens
  useEffect(() => {
    if (open && clusterId) {
      fetchData();
    }
  }, [open, clusterId]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch ENMs and cluster data in parallel
      const [enmsResponse, clusterResponse] = await Promise.all([
        api.get('/api/enm'),
        api.get(`/api/eiap-cluster/${clusterId}`)
      ]);

      const clusterData = clusterResponse.data;
      const linkedEnmIds = clusterData.linkedEnms || [];

      console.log('Fetched cluster:', clusterData);
      console.log('Current linked ENMs:', linkedEnmIds);

      setEnms(enmsResponse.data);
      setCluster(clusterData);
      setSelectedEnms(linkedEnmIds);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching data.',
          timestamp: new Date(),
          color: 'red',
        },
      ]);
    }
  };

  const handleSave = () => {
    onSave(clusterId, selectedEnms);
  };

  const handleClose = () => {
    setSelectedEnms(cluster?.linkedEnms || []);
    onClose();
  };

  return ReactDOM.createPortal(
    <ReactEDSWrapper
      component={Dialog}
      className={'overFlowDialog'}
      show={open}
      onClose={handleClose}
      title="Link ENMs"
      subtitle={`Configure ENM links for ${cluster?.name || 'cluster'}`}
      bottom={
        <ReactEDSWrapper
          component={Button}
          primary
          onClick={handleSave}
        >
          Save
        </ReactEDSWrapper>
      }
    >
      {open && (
        <div style={{ padding: '24px' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p>Loading data...</p>
            </div>
          ) : (
            <>
              <p style={{ marginBottom: '16px', color: '#6a6a6a' }}>
                Select ENM instances to link with this EIAP cluster. Linked ENMs will be associated with the cluster's endpoints.
              </p>

              <ReactEDSWrapper
                component={Select}
                type="input-multi"
                label="Select ENMs"
                key={`enm-select-${clusterId}-${selectedEnms.length}`}
                value={selectedEnms}
                onChange={(event: any) => {
                  const newValue = event?.target?.value ?? event;
                  console.log('Select onChange - newValue:', newValue);
                  setSelectedEnms(Array.isArray(newValue) ? newValue : []);
                }}
              >
                {enms.map((enm) => (
                  <ReactEDSWrapper key={enm.id} component={MenuItem} value={enm.id}>
                    {`${enm.name} (${enm.category})`}
                  </ReactEDSWrapper>
                ))}
              </ReactEDSWrapper>
            </>
          )}
        </div>
      )}
    </ReactEDSWrapper>,
    document.body
  );
};

export default LinkEnmsDialog;
